import SaveIcon from '@material-ui/icons/Save';
import { useIO, useLinked, useModel } from '@type-r/react';
import { Column, Columns, Flex, FormInput, PageLayout, PrimaryButton, CollapsableSection, Toolbar, TopSection, FormSelect, Option, back, ReadOnly } from 'lqx-react';
import * as React from 'react';
import { CompanyModel } from 'api';
import { EditIdentifiers } from './identifiers';
import { CountryInfo } from 'src/app/services/formdata.service';

export const EditCompany = ({ companyId, companyCountries } : { companyId: string | number, companyCountries: CountryInfo[] }) => {
    const errorMessage = useLinked(null);
    const company = useModel(CompanyModel);
    const disableSave = useLinked(false);

    const isReady = useIO(async () => {
        if (!isNaN(companyId as number)) {
            company.id = companyId.toString();
            await Promise.all([
                company.fetch({
                    params: {
                        deepLoad: true
                    }
                } as any)
            ]).catch(e => console.error(e));
        }
    });

    return (
        <PageLayout
            subheader={
                <Toolbar>
                    <Flex grow />
                    {errorMessage.value ?
                        <span>{errorMessage.value.message}</span>
                        :
                        null
                    }

                    {isReady ?
                        <PrimaryButton
                            variant="contained"
                            startIcon={<SaveIcon />}
                            disabled={!company.isValid() || disableSave.value}
                            onClick={() => {
                                errorMessage.set(null);
                                if (company.isValid()) {
                                    disableSave.set(true);
                                    company
                                        .save()
                                        .catch((err) => {
                                            errorMessage.set(err); 
                                            disableSave.set(false);
                                        });
                                } else {
                                    errorMessage.set({ message: 'All fields must be properly filled' });
                                }
                                disableSave.set(false);
                                back();
                            }}
                        >
                            Save
                        </PrimaryButton>
                        :
                        null
                    }
                </Toolbar>

            }
        >
            {isReady ?
                <EditCompanyInformation
                    company={company}
                    companyCountries = {companyCountries}
                />
            :
                <Flex center stretch>
                    Loading...
                </Flex>
            }
        </PageLayout>
    )
};

const EditCompanyInformation = ({ company, companyCountries } : { company: CompanyModel, companyCountries: CountryInfo[] }) => {
    return (

        <TopSection title="Company Information">
            <ReadOnly.Provider value={ !!company.lqxId }>
                <CollapsableSection title="General Information">
                    <Columns>
                        <Column>
                            <FormInput
                                $value={company.$.name}
                                required
                                label="Name"
                                trimOnBlur
                            />
                            <FormInput
                                $value={company.$.shortname}
                                label="Company Shortname"
                                trimOnBlur
                            />
                            <FormInput
                                    value={company.inblockId}
                                    label="Inblock ID"
                            />
                            <FormInput
                                    value={company.lqxId}
                                    label="LiquidX ID"
                            />
                        </Column>
                        <Column>
                            <FormInput
                                $value={company.$.dba}
                                label="DBA"
                                trimOnBlur
                            />
                            <FormInput
                                $value={company.$.entityType}
                                label="Entity Type"
                                trimOnBlur
                            />
                            <FormInput
                                $value={company.$.taxid}
                                label="Tax ID"
                                trimOnBlur
                            />
                        </Column>
                    </Columns>
                </CollapsableSection>
                <CollapsableSection
                    title="Location Information"
                >
                    <Columns>
                        <Column>
                            <FormInput
                                $value={company.$.addressLine1}
                                required
                                label="Address Line One"
                                trimOnBlur
                            />

                            <FormInput
                                $value={company.$.city}
                                required
                                label=" City"
                                trimOnBlur
                            />

                            <FormSelect $value={company.$.country} label="Country" >
                              { companyCountries.map(cc => <Option value={ cc.code }>{ cc.name }</Option>) }
                            </FormSelect>

                        </Column>
                        <Column>

                            <FormInput
                                $value={company.$.addressLine2}
                                label="Address Line Two"
                                trimOnBlur
                            />
                            <FormInput
                                $value={company.$.stateRegion}
                                required
                                label="State/Region"
                                trimOnBlur
                            />

                            <FormInput
                                $value={company.$.postalCode}
                                required
                                label="Postal Code"
                                trimOnBlur
                            />
                        </Column>
                    </Columns>
                </CollapsableSection>
            </ReadOnly.Provider>
            <CollapsableSection
                title="Other"
            >
                <Columns>
                    <Column>
                        <FormInput
                            $value={company.$.duns}
                            label="Duns Number"
                            trimOnBlur
                        />
                    </Column>
                    <Column>
                        <FormInput
                            $value={company.$.ultimateDuns}
                            label="Ultimate Duns Number"
                            trimOnBlur
                        />
                    </Column>
                </Columns>
                <EditIdentifiers company = {company}/>
            </CollapsableSection>
        </TopSection>
    )
}
